import { useRouteLoaderData } from 'react-router';
import { type loader as rootLoader } from '#app/root.tsx';

function isUser(
  user: any,
): user is Awaited<ReturnType<typeof rootLoader>>['data']['user'] {
  return user && typeof user === 'object' && typeof user.id === 'string';
}

export function useOptionalUser() {
  const data = useRouteLoaderData<typeof rootLoader>('root');
  if (!data || !isUser(data.user)) {
    return undefined;
  }
  return data.user;
}

export function useUser() {
  const maybeUser = useOptionalUser();
  if (!maybeUser) {
    throw new Error(
      'No user found in root loader, but user is required by useUser. If user is optional, try useOptionalUser instead.',
    );
  }
  return maybeUser;
}

type Action =
  | 'create'
  | 'read'
  | 'update'
  | 'delete'
  | 'list'
  | 'view'
  | 'administrate'
  | 'moderate'
  | 'settings'
  | 'assign-permissions'
  | 'assign-roles'
  | 'manage'
  | 'target-roles';
type Entity =
  | 'user'
  | 'admin'
  | 'kbase'
  | 'cbmetatag'
  | 'cbquiz'
  | 'dashboard';
type Access = 'own' | 'any' | 'assigned' | 'public' | 'own,any' | 'any,own';
export type PermissionString =
  | `${Entity}:${Action}`
  | `${Entity}:${Action}:${Access}`;

export function parsePermissionString(permissionString: PermissionString) {
  const [entity, action, access] = permissionString.split(':') as [
    Entity,
    Action,
    Access | undefined,
  ];
  return {
    action,
    entity,
    access: access ? (access.split(',') as Array<Access>) : undefined,
  };
}

export function userHasPermission(
  user: Pick<ReturnType<typeof useUser>, 'roles'> | null | undefined,
  permission: PermissionString,
) {
  if (!user) return false;

  // Check if user has admin role first
  const isAdmin = userHasRole(user, 'admin');
  if (isAdmin) return true;

  const { action, entity, access } = parsePermissionString(permission);
  return user.roles.some((role) =>
    role.permissions.some(
      (permission) =>
        permission.entity === entity &&
        permission.action === action &&
        (!access || access.includes(permission.access)),
    ),
  );
}

export function userHasRole(
  user: Pick<ReturnType<typeof useUser>, 'roles'> | null,
  role: string,
) {
  if (!user) return false;
  return user.roles.some((r) => r.name === role);
}
